import Vue from 'vue';
import ApiClient from '../client';
import Config from '../../services/config.service';
const config = new Config();
import { ErrorService } from '@/services/error-service.js';
import { userSession } from '@/api/modules/login.api.js';
const path = require('path');

export function withOrganisationListFilters(filters = {}) {
  const next = { ...filters };
  if (userSession.isUserAuthenticated && !userSession.isSuperuser()) {
    next.user__role__in = [2];
  }
  return next;
}

/**
 * CurrentOrganisation observable.
 * @type {Vue.Observable<Object>}
 */
export const currentOrganisation = Vue.observable({
  value: null,
  quota: null
});

class OrganisationAPI extends ApiClient {

  constructor() {
    super();
  }

  async getOrganisationsList(filters, page = 1) {
    filters = withOrganisationListFilters(filters);
    let route = path.join(
      this.ORGANISATION_API_PATH,
      `organisations/?page_size=${config.itemsPerPage.organisations}&page=${page}`
    );
    for (const filter in filters) {
      if (filters[filter] && filters[filter].length) {
        route = route.concat('', `&${filter}=${filters[filter].join(',')}`);
      }
    }
    const response = await this.get(route);
    if (response.status === 200) {
      return response.data;
    }
    return false;
  }

  async orderOrganisationsList(direction, field, filters, page = 1) {
    filters = withOrganisationListFilters(filters);
    let route = path.join(
      this.ORGANISATION_API_PATH,
      `organisations/?page_size=${config.itemsPerPage.organisations}&ordering=${direction}${field}&page=${page}`
    );
    for (const filter in filters) {
      if (filters[filter] && filters[filter].length) {
        route = route.concat('', `&${filter}=${filters[filter].join(',')}`);
      }
    }
    const response = await this.get(route);
    if (response.status === 200) {
      return response.data;
    }
    return false;
  }

  async getOrganisation(id) {
    const route = path.join(
      this.ORGANISATION_API_PATH,
      `organisations/${id}/`
    );
    const response = await this.get(route);
    if (response.status === 200) {
      currentOrganisation.value = response.data;
      if(config.isSaas) {
        await this.getOrganisationQuota(id);
      }
      return response.data;
    }
    return false;
  }

  async getOrganisationQuota(id) {
    const route = path.join(
      this.ORGANISATION_API_PATH,
      `organisations/${id}/quota/`
    );
    const response = await this.get(route);
    if (response.status === 200 ) {
      currentOrganisation.quota = response.data;
    } else {
      currentOrganisation.quota = null;
    }
  }

  async createOrganisation(data) {
    const route = path.join(
      this.ORGANISATION_API_PATH,
      'organisations/'
    );
    const response = await this.post(route, data);
    if (response.status === 201) {
      return response.data;
    }
    return false;
  }

  async updateOrganisation(id, data) {
    const route = path.join(
      this.ORGANISATION_API_PATH,
      `organisations/${id}/`
    );
    const response = await this.put(route, data);
    if (response.status === 200) {
      return response.data;
    }
    return false;
  }

  async setOrganisationAgreement(id, data) {
    const route = path.join(
      this.ORGANISATION_API_PATH,
      `organisations/${id}/agreement/`
    );
    const response = await this.put(route, data);
    if (response.status === 200) {
      return response.data;
    }
    return false;
  }

  async removeUserFromOrganisation(usergroupId, userId) {
    try {
      const route = 
        `/usergroup/user-groups/${usergroupId}/members/${userId}/`;
      const response = await this.delete(route);
      return response.status === 204;
    } catch (err) {
      ErrorService.onError(err);
      return false;
    }
  }

  async searchOrganisation(text) {
    const route = path.join(
      this.ORGANISATION_API_PATH,
      `user-groups/?search=${text}`
    );
    const response = await this.get(route);
    if (response.status === 200) {
      return response.data;
    }
    return false;
  }

  async deleteOrganisation(id) {
    const route = path.join(
      this.ORGANISATION_API_PATH,
      `organisations/${id}/`
    );
    const response = await this.delete(route);
    if (response.status === 204) {
      return response.data;
    }
    return false;
  }

  async getOrganisationsTypes() {
    const route = path.join(
      this.ORGANISATION_API_PATH,
      'organisation-types/'
    );
    const response = await this.get(route);
    if (response.status === 200) {
      return response.data;
    }
    return false;
  }

  // JURIDICTIONS
  async getJurisdictionsList(page) {
    const route = path.join(
      this.ORGANISATION_API_PATH,
      `jurisdictions/?page_size=${config.itemsPerPage.jurisdictions}&page=${page}`
    );
    const response = await this.get(route);
    if (response.status === 200) {
      return response.data;
    }
    return false;
  }
  async searchJurisdictionsList(query, controller) {
    const route = path.join(
      this.ORGANISATION_API_PATH,
      `jurisdictions/?search=${query}`
    );
    const response = await this.get(
      route,
      { signal: controller.signal }
    );
    return response;
  }
  async getJurisdiction(juridictionId) {
    const route = path.join(
      this.ORGANISATION_API_PATH,
        
      `jurisdictions/${juridictionId}`
    );
    const response = await this.get(route);
    if (response.status === 200) {
      return response.data;
    }
    return false;
  }
  async createJurisdiction(data) {
    const route = path.join(
      this.ORGANISATION_API_PATH,
      'jurisdictions/'
    );
    const response = await this.post(route, data);
    if (response.status === 201) {
      return response.data;
    }
    return false;
  }
  async updateJurisdiction(juridictionId, data) {
    const route = path.join(
      this.ORGANISATION_API_PATH,
      `jurisdictions/${juridictionId}/`
    );
    const response = await this.put(route, data);
    if (response.status === 200) {
      return response.data;
    }
    return false;
  }
  async deleteJurisdiction(juridictionId) {
    const route = path.join(
      this.ORGANISATION_API_PATH,
      `jurisdictions/${juridictionId}/`
    );
    const response = await this.delete(route);
    if (response.status === 204) {
      return response.data;
    }
    return false;
  }

  async getMunicipalities() {
    const route = path.join(
      this.ORGANISATION_API_PATH,
      'municipalities/'
    );
    const response = await this.get(route);
    if (response.status === 200) {
      return response.data;
    }
    return false;
  }
  async searchMunicipalities(query) {
    const route = path.join(
      this.ORGANISATION_API_PATH,
      `municipalities/?search=${query}`
    );
    const response = await this.get(route);
    if (response.status === 200) {
      return response.data;
    }
    return false;
  }
}

const organisationAPI = new OrganisationAPI();
export default organisationAPI;
